import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Drawer.css';

type DrawerSize = 'sm' | 'md' | 'lg' | 'xl';

interface DrawerProps {
    /** Controls whether the drawer is open (mounted + visible). */
    open: boolean;
    /** Fires on backdrop click, ESC, or close-button click. */
    onClose: () => void;
    /** Title shown in the drawer header. */
    title?: React.ReactNode;
    /** Optional subtitle below the title. */
    subtitle?: React.ReactNode;
    /** Body content. */
    children?: React.ReactNode;
    /** Sticky footer (Save/Cancel/Delete row). */
    footer?: React.ReactNode;
    /** Width preset. Default `md` (480px). */
    size?: DrawerSize;
    /** Set false to disable closing on backdrop click. Default `true`. */
    closeOnBackdrop?: boolean;
    /** Set false to disable closing on ESC. Default `true`. */
    closeOnEsc?: boolean;
    /** Optional extra class for the panel surface. */
    panelClassName?: string;
    /** Optional Cypress selector for the drawer root. Defaults to `'drawer'`. */
    dataCy?: string;
}

/**
 * Reusable slide-over panel. Renders into document.body via a portal so it
 * always sits above page chrome and never gets clipped by ancestor
 * `overflow:hidden`.
 *
 * Behaviour:
 *   - Slides in from the right with a transform; backdrop fades in.
 *   - ESC and backdrop click both invoke `onClose`.
 *   - While open, document body scroll is locked so the page behind doesn't
 *     scroll along with the drawer.
 *   - Animates out before unmounting (200ms) so the close transition reads.
 */
const Drawer: React.FC<DrawerProps> = ({
    open,
    onClose,
    title,
    subtitle,
    children,
    footer,
    size = 'md',
    closeOnBackdrop = true,
    closeOnEsc = true,
    panelClassName,
    dataCy,
}) => {
    const [render, setRender] = React.useState(open);
    const [animateIn, setAnimateIn] = React.useState(false);
    const panelRef = useRef<HTMLDivElement>(null);

    // Mount/unmount with animation: when `open` flips, we keep the DOM around
    // for ~200ms so the slide-out transition can play.
    useEffect(() => {
        if (open) {
            setRender(true);
            // Next frame so the initial transform applies before the animated state.
            const id = requestAnimationFrame(() => setAnimateIn(true));
            return () => cancelAnimationFrame(id);
        }
        setAnimateIn(false);
        const t = window.setTimeout(() => setRender(false), 220);
        return () => window.clearTimeout(t);
    }, [open]);

    // Lock body scroll while open.
    useEffect(() => {
        if (!render) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [render]);

    // ESC to close.
    useEffect(() => {
        if (!open || !closeOnEsc) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, closeOnEsc, onClose]);

    if (!render) return null;

    return createPortal(
        <div className={`gs-drawer ${animateIn ? 'gs-drawer--open' : ''}`} data-cy={dataCy ?? 'drawer'}>
            <div
                className="gs-drawer__backdrop"
                data-cy="drawer-overlay"
                onClick={() => {
                    if (closeOnBackdrop) onClose();
                }}
                aria-hidden
            />
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? 'gs-drawer-title' : undefined}
                className={`gs-drawer__panel gs-drawer__panel--${size} ${
                    panelClassName ?? ''
                }`}
            >
                {(title || subtitle) && (
                    <div className="gs-drawer__header">
                        <div className="gs-drawer__titles">
                            {title && (
                                <h2
                                    id="gs-drawer-title"
                                    className="gs-drawer__title"
                                >
                                    {title}
                                </h2>
                            )}
                            {subtitle && (
                                <div className="gs-drawer__subtitle">{subtitle}</div>
                            )}
                        </div>
                        <button
                            type="button"
                            className="gs-drawer__close"
                            data-cy="drawer-close-btn"
                            onClick={onClose}
                            aria-label="Close"
                            title="Close"
                        >
                            ×
                        </button>
                    </div>
                )}

                <div className="gs-drawer__body">{children}</div>

                {footer && <div className="gs-drawer__footer">{footer}</div>}
            </div>
        </div>,
        document.body
    );
};

export default Drawer;
